import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { useTour } from '../context/TourContext';

const STEPS = [
  { path: '/dashboard', sel: '[data-tour="portfolio-totals"]', title: 'Portfolio at a glance',
    body: 'Total invested, current value and overall return across every farm you own, valued by the Farmway team.' },
  { path: '/dashboard', sel: '[data-tour="portfolio-chart"]', title: 'Performance over time',
    body: 'One combined chart of your whole portfolio value, built from the dated valuation history of each holding.' },
  { path: '/dashboard', sel: '[data-tour="holdings-table"]', title: 'Your real holdings',
    body: 'Each row is a real farm position. Open one for its value history, trades and documents, or hit Sell to list it on the marketplace.' },
  { path: '/dashboard', sel: '[data-tour="reports-link"]', title: 'Monthly reports',
    body: 'Every monthly statement is archived here, and you can download a live statement any time.' },
  { path: '/marketplace', sel: '[data-tour="market-grid"]', title: 'Secondary marketplace',
    body: 'Buy and sell whole positions with other Farmway investors, with past-sale insights on every listing.' },
  { path: '/marketplace', sel: '[data-tour="market-tabs"]', title: 'Track your activity',
    body: 'My activity shows your own listings with incoming bids to accept, plus every bid you have placed.' },
  { path: '/reports', sel: '[data-tour="reports-list"]', title: 'Statement archive',
    body: 'Monthly statements are archived automatically. Download any month as a branded PDF. That is the tour, enjoy Farmway!' },
];

export default function TourGuide() {
  const { active, stopTour } = useTour();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { if (active) setIdx(0); }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    const step = STEPS[idx];
    if (location.pathname !== step.path) {
      navigate(step.path);
      return undefined;
    }
    let timer;
    let tries = 0;
    let el = null;
    const measure = () => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    const find = () => {
      el = document.querySelector(step.sel);
      if (el) {
        el.scrollIntoView({ block: 'center' });
        timer = setTimeout(measure, 350);
      } else if (tries < 25) {
        tries += 1;
        timer = setTimeout(find, 160);
      } else if (idx < STEPS.length - 1) {
        setIdx(idx + 1);
      } else {
        stopTour();
      }
    };
    setRect(null);
    find();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, idx, location.pathname, navigate, stopTour]);

  if (!active || !rect) return null;

  const step = STEPS[idx];
  const pad = 8;
  const cardW = 340;
  const below = rect.top + rect.height + 230 < window.innerHeight;
  const cardTop = below ? rect.top + rect.height + pad + 12 : Math.max(12, rect.top - 215);
  const cardLeft = Math.min(Math.max(12, rect.left + rect.width / 2 - cardW / 2), window.innerWidth - cardW - 12);
  const end = () => { stopTour(); setIdx(0); };

  return (
    <>
      <div className="fixed inset-0 z-[98]" aria-hidden="true" />
      <div
        className="pointer-events-none fixed z-[99] rounded-2xl border-2 border-farmway-gold/70 transition-all duration-300"
        style={{
          top: rect.top - pad, left: rect.left - pad,
          width: rect.width + pad * 2, height: rect.height + pad * 2,
          boxShadow: '0 0 0 9999px rgba(4, 18, 11, 0.78)',
        }}
      />
      <div
        data-testid="tour-card"
        className="fixed z-[100] rounded-2xl border border-white/10 bg-farmway-green-dark p-5 shadow-2xl"
        style={{ top: cardTop, left: cardLeft, width: cardW }}
      >
        <div className="flex items-start justify-between gap-3">
          <span className="eyebrow">Step {idx + 1} of {STEPS.length}</span>
          <button data-testid="tour-skip-btn" onClick={end} aria-label="End tour" className="text-farmway-cream/40 hover:text-farmway-cream">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="mt-2 font-display text-lg font-black uppercase tracking-tight text-farmway-cream">{step.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-farmway-cream/70">{step.body}</p>
        <div className="mt-4 flex items-center justify-between">
          <button
            data-testid="tour-back-btn"
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-farmway-cream/60 hover:text-farmway-cream disabled:opacity-30"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <button
            data-testid="tour-next-btn"
            onClick={() => (idx < STEPS.length - 1 ? setIdx(idx + 1) : end())}
            className="inline-flex items-center gap-2 rounded-full bg-farmway-gold px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-farmway-green"
          >
            {idx < STEPS.length - 1 ? 'Next' : 'Finish'} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </>
  );
}
